import { fetchIsPostalCodeValid } from '@/entities/facility';
import { checkToFormatPhoneNumber, checkPhoneNumber, validateEmail } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';

/**
 * Validates the shipping contact of a shipment and collects every field error before saving. Checks phone and email format plus required country, address, name, city and state, then for US and CA addresses resolves the state's division code and verifies the zip through the postal-code service, marking the zip invalid when the lookup throws.
 *
 * @param contacts - Shipment contact fields being validated.
 * @param listStatesUS - US state divisions used to resolve the division code.
 * @param listStatesCA - Canadian province divisions used to resolve the division code.
 * @returns A map of field name to validation message, empty when the contact is valid.
 */
const checkShipmentData = async ({
  contacts,
  listStatesUS,
  listStatesCA,
}: {
  contacts?: any;
  listStatesUS?: any[];
  listStatesCA?: any[];
}) => {
  const errorObject: any = {};
  if (contacts?.phone) {
    if (!checkPhoneNumber(contacts?.phone)) {
      _.assign(errorObject, { phone: t`Invalid value` });
    }
  } else {
    _.assign(errorObject, { phone: t`Phone number is required.` });
  }

  //* validate emails
  if (contacts.email) {
    if (!validateEmail(contacts.email)) {
      _.assign(errorObject, {
        email: t`The field contains email with invalid format, please check again!`,
      });
    }
  } else {
    _.assign(errorObject, { email: t`Email is required.` });
  }

  if (!contacts.country) {
    _.assign(errorObject, { country: t`Country is required.` });
  }

  if (!contacts.street1) {
    _.assign(errorObject, { street1: t`Address is required.` });
  }

  if (!contacts.name) {
    _.assign(errorObject, { name: t`Name is required.` });
  }

  if (!contacts.city) {
    _.assign(errorObject, { city: t`City is required.` });
  }

  if (!contacts.state) {
    _.assign(errorObject, { state: t`State is required.` });
  }

  // *: Validate shipping zipcode
  try {
    if (checkToFormatPhoneNumber(contacts.country)) {
      const nzipCode =
        contacts.country === 'CA' && contacts.zip ? contacts.zip.split(' ')[0] : contacts.zip;
      if (nzipCode) {
        const divisions = contacts.country === 'CA' ? listStatesCA : listStatesUS;
        /**
         * The state or province entry matching the contact's state, taken from the country-specific divisions list. Located with _.find, accepting either an exact match on the entry's value or a label that contains the contact's state string.
         *
         * @returns The matching division entry, or undefined.
         */
        const division = _.find(
          divisions,
          (x) => x.value === contacts.state || _.includes(x.label, contacts.state),
        );
        const isPostalCodeValid = await fetchIsPostalCodeValid({
          division: division.fullCode,
          postalCode: nzipCode,
        });
        if (!isPostalCodeValid) {
          _.assign(errorObject, { zip: t`Invalid zipcode` });
        }
      } else {
        _.assign(errorObject, { zip: t`Zip code is required.` });
      }
    } else if (!contacts.zip) {
      _.assign(errorObject, { zip: t`Zip code is required.` });
    }
  } catch (error) {
    console.error('Failed to check zip code: ', error);
    _.assign(errorObject, { zip: t`Invalid zipcode` });
  }
  return errorObject;
};

export { checkShipmentData };
